import { message } from "@/utils/message.js";
import moment from "moment";
import i18n from "@/i18n";
import parsePhoneNumberFromString from "libphonenumber-js";

const utils = {
  /**
   * @returns {Promise<{code:number; message?:string}>} code 1 可以访问外网，0不可以访问外网
   */
  isOuterLine() {
    return new Promise((resolve, reject) => {
      if (window.FB?.init) {
        resolve({ code: 1 });
      } else {
        resolve({ code: 0, message: "当前网络环境无法登录Facebook，请检查网络！" });
      }
    });
  },
  /**
   * @param {*} data 可以是任何数据类型
   * @returns {string} object/array/string/boolean/number/undefined/null
   */
  isTypeOf(data) {
    return Object.prototype.toString
      .call(data)
      .replace(/\[object (\w+)\]/, "$1")
      .toLowerCase();
  },
  /**
   * 随机字符串
   * @param {number} length
   * @returns
   */
  getRandomString(length) {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters.charAt(randomIndex);
    }
    return result;
  },
  /**
   * @param {*} timestamp 时间戳
   * @returns
   */
  getTimeAgo(timestamp) {
    const now = new Date().getTime();
    const diff = now - timestamp;

    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;

    if (diff < minute) {
      return "刚刚";
    } else if (diff < hour) {
      const minutesAgo = Math.floor(diff / minute);
      return `${minutesAgo}分钟前`;
    } else if (diff < day) {
      const hoursAgo = Math.floor(diff / hour);
      return `${hoursAgo}小时前`;
    } else {
      const daysAgo = Math.floor(diff / day);
      return `${daysAgo}天前`;
    }
  },
  /**
   * 数字千分位分隔符号，并保留指定位数小数，默认保留两位小数
   * @param {(string | number)} n 要转换的数字
   * @param {number} p 要保留的小数点位数位数
   * @returns {string}
   */
  toThousandsNew(n = 0, p = 2) {
    // 更加健壮的数字转换
    let num = parseFloat(n);

    // 验证是否为有效数字
    if (isNaN(num)) {
      // throw new Error("Invalid number input");
      num = 0;
    }

    // 验证并校正小数位数
    const fractionDigits = Math.max(0, Math.min(20, Math.round(p)));

    // 格式化数字
    return num.toLocaleString("zh-CN", {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  },

  /**
   * 银行家舍入法（四舍六入五成双）
   * IEEE 754 推荐的货币舍入标准，避免传统四舍五入在统计上产生系统性向上偏差
   * @param {number} value    要舍入的数值
   * @param {number} decimals 要保留的小数位数，默认 2
   * @returns {number}
   */
  roundHalfEven(value, decimals = 2) {
    if (typeof value !== "number" || isNaN(value) || !isFinite(value)) {
      return value;
    }

    decimals = Math.max(0, Math.floor(decimals));
    const factor = 10 ** decimals;
    const sign = value < 0 ? -1 : 1;
    const absValue = Math.abs(value);

    const scaled = absValue * factor;
    const intPart = Math.floor(scaled);
    const fractionalPart = scaled - intPart;

    // 浮点数漂移容差（1e-10 远大于双精度 ~1e-15 的漂移量，又远小于 0.005 的关注差异）
    const EPSILON = 1e-10;
    const isHalf = Math.abs(fractionalPart - 0.5) < EPSILON;

    let roundedInt;
    if (isHalf) {
      // 恰好为 0.5：五成双
      roundedInt = intPart % 2 === 0 ? intPart : intPart + 1;
    } else if (fractionalPart < 0.5) {
      roundedInt = intPart;
    } else {
      roundedInt = intPart + 1;
    }

    return (sign * roundedInt) / factor;
  },

  /**
   * 实现复制文本功能，兼容性写法
   */
  _copy(value) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(value)
        .then(function () {
          message({
            type: "success",
            message: "已复制",
          });
        })
        .catch(function (err) {
          message({
            type: "error",
            message: `复制失败：${err}`,
          });
        });
    } else {
      var textArea = document.createElement("textarea");
      textArea.value = value;
      textArea.style.display = "none";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        var successful = document.execCommand("copy");
        if (successful) {
          message({
            type: "success",
            message: "已复制",
          });
        } else {
          message({
            type: "error",
            message: `复制失败`,
          });
        }
      } catch (err) {
        message({
          type: "error",
          message: `复制失败：${err}`,
        });
      }
      document.body.removeChild(textArea); // 移除临时创建的 textarea
    }
  },
  /**
   * 机器人标签 effect 类型
   * @param {1|2|3} cs_type 机器人类型
   */
  filterBotTagType(cs_type) {
    let s;
    switch (cs_type) {
      case "1":
        s = "";
        break;
      case "2":
        s = "important";
        break;
      case "3":
        s = "success";
        break;
      default:
        break;
    }
    return s;
  },
  /**
   * 机器人名称
   * @param {1|2|3} cs_type 机器人类型
   */
  filterBotName(cs_type) {
    const typeKeys = {
      "1": "bot-list.Bot-Type-Chat",
      "2": "bot-list.Bot-Type-Page",
      "3": "bot-list.Bot-Type-Email",
    }
    return i18n.t(typeKeys[String(cs_type)] || "")
  },

  /**
   * 以可命名的授权弹窗形式打开链接；同名窗口会被复用，避免重复创建第三方授权页。
   * @param {string} path 链接地址
   * @param {string} [name="_blank"] 窗口名称
   * @returns {Window | null}
   */
  openNewWindow(path, name = "_blank") {
    const { availTop, availLeft, availWidth, availHeight } = window.screen;
    const windowHeight = Math.min(availHeight * 0.8, 800);
    const windowWidth = Math.min(availWidth * 0.8, 1200);
    let iTop = (availHeight - windowHeight) / 2;
    let iLeft = (availWidth - windowWidth) / 2;
    if (navigator.userAgent.indexOf("Chrome") !== -1) {
      iTop += availTop;
      iLeft += availLeft;
    }

    return window.open(path, name, `popup,width=${windowWidth},height=${windowHeight},left=${iLeft},top=${iTop}`);
  },
  /**
   * base64文件转 blob
   * @param {string} base64String 文件base64
   * @returns {Blob}
   */
  base64ToBlob(base64String) {
    let arr = base64String.split(","),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  },

  /**
   * 时间日期格式化
   * @param {string} date 要格式化的时间
   * @param {string} format
   * @returns
   */
  formatDateTime(date, format = "YYYY-MM-DD HH:mm:ss") {
    return moment(date).format(format);
  },
  /**
   * whatsapp模板变量高亮
   * @param {string} str
   * @returns {string}
   */
  whatsappTemplateVariableHighlight(str) {
    if (str) {
      let regex = /{{(.*?)}}/g;
      return str.replace(regex, '<span class="whatsapp-template-variable-highlight">{{$1}}</span>');
    } else {
      return "";
    }
  },

  /**
   * whatsapp模板组件变量列表
   * @param {string} str
   * @returns {string[]}
   */
  whatsappTemplateVariableList(str) {
    let regex = /{{(.*?)}}/g;
    let matches = [];
    let match;

    // 使用循环提取所有匹配的内容
    while ((match = regex.exec(str)) !== null) {
      matches.push(match[1].trim());
    }

    return matches;
  },
  /**
   * 格式化最后聊天时间
   * @description 接收一个日期字符串作为参数，首先将当前日期和时间设定为基准，然后分别检查传入的日期是否为今天、昨天、前天、本周、今年等，并返回相应格式的字符串。
   * @param {string} last_chat_time
   * @returns {string}
   */
  format_last_chat_time(last_chat_time) {
    if (!last_chat_time) {
      return "";
    }
    const date = new Date(last_chat_time);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    const dayBeforeYesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    dayBeforeYesterday.setDate(today.getDate() - 2);

    const daysOfWeek = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];

    if (date >= today) {
      return `${date.getHours()}:${date.getMinutes()}`;
    } else if (date >= yesterday) {
      // return `昨天 ${date.getHours()}:${date.getMinutes()}`;
      return `昨天`;
    } else if (date >= dayBeforeYesterday) {
      // return `前天 ${date.getHours()}:${date.getMinutes()}`;
      return `前天`;
    } else if (date >= new Date(today.setDate(today.getDate() - today.getDay()))) {
      return daysOfWeek[date.getDay()];
    } else if (date.getFullYear() === now.getFullYear()) {
      return `${date.getMonth() + 1}月${date.getDate()}日`;
    } else {
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    }
  },
  /**
   * 设置首字母大写
   * @param {string} str
   * @returns {string}
   */
  capitalizeWords(str) {
    return str
      .split(" ")
      .map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ");
  },

  /**
   * 设置日期间隔
   * @param {number} days
   * @returns {string[]}
   */
  setDateRange(days) {
    let currentData = moment(new Date(Date.now())).subtract(1, "days").format("yyyy-MM-DD");
    let prevDate = moment(new Date(Date.now())).subtract(days, "days").format("yyyy-MM-DD");
    return [prevDate, currentData];
  },

  /**
   * 格式化数字 以万为单位
   * @param {number} num
   * @returns {string}
   * */
  formatNumber(numb, fixed = 2) {
    const num = Number(numb);
    if (typeof Number(num) !== "number" || isNaN(num)) {
      return num;
    }
    if (num >= 10000) {
      return (num / 10000).toFixed(fixed) + "万";
    }
    return utils.toThousandsNew(num, 0);
  },

  _aliCaptchaLoaded: false,
  _loadAliCaptchaScript() {
    if (this._aliCaptchaLoaded) return Promise.resolve()
    if (window.initAliyunCaptcha) {
      this._aliCaptchaLoaded = true
      return Promise.resolve()
    }
    return new Promise((resolve, reject) => {
      window.AliyunCaptchaConfig = {
        region: "cn",
        prefix: "o4dcro"
      }
      var _clear = console.clear
      console.clear = function () {}
      var script = document.createElement("script")
      script.src = "https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js"
      script.onload = function () {
        console.clear = _clear
        utils._aliCaptchaLoaded = true
        resolve()
      }
      script.onerror = function () {
        console.clear = _clear
        reject(new Error("AliyunCaptcha 加载失败"))
      }
      document.head.appendChild(script)
    })
  },

  /**
   * 初始化阿里人机验证
   * @param param0
   * @param {string} param0.element
   * @param {string} param0.button
   * @param {(instance:any)=>void} param0.getInstance
   * @param {(captchaVerifyParam:any)=>void} param0.onSuccess
   * @param {(error:any)=>void} param0.onError
   * @param {()=>void} param0.onClose
   */
  initAliCaptcha(param0) {
    this._loadAliCaptchaScript().then(function () {
      const config = {
        SceneId: "130neupx",
        mode: "popup",
        element: param0.element,
        success: param0.onSuccess,
        fail: param0.onError,
        onError: param0.onError,
        onClose: param0.onClose,
        getInstance: param0.getInstance,
        slideStyle: {
          width: 360, height: 40,
        },
        language: "cn"
      }
      if (param0.button) {
        config.button = param0.button
      }
      window.initAliyunCaptcha(config)
    })
  },
  /**
   * 获取静态资源桶
   * @returns {string}
   * @example https://joindigital.oss-accelerate.aliyuncs.com/joinmc_images/  下面地址在oss 上要跟项目中的地址一致。
   * @description 这个地址实际上取代了 /src/assets/images，现有资源的文件夹结构和桶里保持一致
   */
  get imageRoot() {
    return process.env.VUE_APP_IMAGE_ROOT;
  },

  /**
   * 格式化国际手机号码
   * @param phone_number
   */
  formatPhoneNumber(phone_number) {
    if (phone_number === null || phone_number === undefined || phone_number === "") {
      return "-";
    }
    const raw = String(phone_number).trim();
    // 没有国家区号前缀(+号)时补上，保证 libphonenumber-js 能识别区号
    const fullNumber = raw.startsWith("+") ? raw : `+${raw}`;
    const phoneNumber = parsePhoneNumberFromString(fullNumber);
    // 解析失败或校验不过时原样返回，保证任意格式都能正常显示
    return phoneNumber && phoneNumber.isValid() ? phoneNumber.formatInternational() : raw;
  },


  /**
   * 格式化文件大小，不足 1KB 显示 B，不足 1MB 显示 KB，1MB 及以上显示 MB
   * @param {number} bytes 文件字节数
   * @returns {string}
   */
  formatFileSize(bytes) {
    if (bytes === undefined || bytes === null) return "-";
    const kb = bytes / 1024;
    if (kb < 1) {
      return `${bytes}B`;
    }
    if (kb < 1024) {
      return `${kb.toFixed(0)}KB`;
    }
    return `${(kb / 1024).toFixed(2)}MB`;
  },
};
export default utils;
